import {
  GridAggregationFunction,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridTreeNodeWithRender,
  GridValueFormatterParams,
} from '@mui/x-data-grid-premium';
import { Chip, styled, SxProps, Theme } from '@mui/material';
import { GroupTableAggregationPossitions, TypeGroupTable } from '../groupTable.component';
import { TypeTable } from '../../table/table.component';
import { Theme as theme } from '../../../styles/theme/theme';

const getFieldNameFromId = (id?: GridRowId): string => {
  // example1: if id = 'auto-generated-row-orderNetTotal/37.97' then fieldName = 'orderNetTotal'
  // example2: if id = 'auto-generated-row-internalOrFullName/Kristina-orderDate/2024-05-31' then fieldName = 'orderDate'
  const cleanId = String(id).replace(/<[^>]*>/g, ''); // delete useless HTML tags

  const allGroupsParts = cleanId.split('/');
  for (let i = allGroupsParts.length - 2; i >= 0; i--) {
    const parts = allGroupsParts[i].split('-');

    if (parts.length > 1 && /^[a-zA-Z]+$/.test(parts[parts.length - 1])) {
      return parts[parts.length - 1];
    }
  }

  return '';
};

export const groupCellStyle = (args: IGroupCellStyle) => {
  const { params, sxGroupCell, sxQuantityCircle, generateGroupCell } = args;
  const groupKey = getFieldNameFromId(params?.id);

  if (groupKey) {
    const groupMember = params?.value;
    const allTableColumns = (params.api as any)?.getAllColumns() as GridColDef[];
    const { headerName: groupName } =
      allTableColumns?.find(({ field }) => field === groupKey) || {};

    const quantity = (params.api as any)?.getRowGroupChildren({ groupId: params?.id })?.length;

    if (generateGroupCell) {
      return generateGroupCell({ groupName, groupMember, quantity });
    }

    return (
      <Wrapper
        sx={{
          ...sxGroupCell,
        }}
      >
        <>{groupName}: &nbsp;</>
        <b>{groupMember}</b>
        <Chip
          size='small'
          color='primary'
          label={quantity}
          sx={{
            ml: '8px',
            height: '20px',
            fontSize: '12px',
            fontWeight: '500',
            minWidth: '21px',
            '& .MuiChip-label': {
              paddingRight: '6px',
              paddingLeft: '6px',
            },
            ...sxQuantityCircle,
          }}
        />
      </Wrapper>
    );
  }
};

export const renderCellForGroupTable = (args: IRenderCellForGroupTable) => {
  const { params, valueComponent, sxGroupCell, sxQuantityCircle } = args || {};

  if (params?.field === '__row_group_by_columns_group__') {
    const groupName = params?.colDef?.headerName;
    const quantity = (params.api as any)?.getRowGroupChildren({ groupId: params?.id })?.length;

    return (
      <Wrapper
        sx={{
          ...sxGroupCell,
        }}
      >
        <>{groupName}: &nbsp;</>
        <b>{valueComponent}</b>
        <Chip
          size='small'
          color='primary'
          label={quantity}
          sx={{
            ml: '8px',
            height: '20px',
            fontSize: '12px',
            fontWeight: '500',
            minWidth: '21px',
            '& .MuiChip-label': {
              paddingRight: '6px',
              paddingLeft: '6px',
            },
            ...sxQuantityCircle,
          }}
        />
      </Wrapper>
    );
  }

  return <>{valueComponent}</>;
};

export const hideUselessArgegationFields = (
  agregationModel: TAgregationModel,
  aggregationPossition: GroupTableAggregationPossitions,
) => {
  const agregationColumns = Object.keys(agregationModel || {});

  if (aggregationPossition === GroupTableAggregationPossitions.both) {
    return {};
  }
  if (aggregationPossition === GroupTableAggregationPossitions.middle) {
    return {
      '& .MuiDataGrid-pinnedRows.MuiDataGrid-pinnedRows--bottom': {
        display: 'none',
      },
    };
  }

  // when aggregationPossition === GroupTableAggregationPossitions.bottom
  const stylesForHideUselessArgegationFields = agregationColumns?.reduce((res, fieldName) => {
    return {
      ...res,
      [`& [class^=${fieldName}-auto-generated-row]`]: {
        color: theme.palette.blue.light,
      },
    };
  }, {} as SxProps<Theme>);

  return stylesForHideUselessArgegationFields;
};

export const customSumAggregation = (args: { returnZero?: boolean } = {}) => {
  const { returnZero = false } = args;
  return {
    apply: ({ values }) => {
      if (!values.length) return returnZero ? 0 : undefined;

      const res = values.reduce((acc: number, item) => {
        const value = item ?? 0;
        acc += value;
        return acc;
      }, 0);
      if (res === 0) {
        return returnZero ? res : undefined;
      } else return res;
    },
    label: '',
    columnTypes: ['number'],
  } as GridAggregationFunction<number, number | null>;
};

const Wrapper = styled('span')(() => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

interface IRenderCellForGroupTable
  extends Pick<TypeGroupTable, 'sxQuantityCircle' | 'sxGroupCell'> {
  params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>;
  valueComponent: JSX.Element;
}

interface IGroupCellStyle
  extends Pick<TypeGroupTable, 'sxQuantityCircle' | 'sxGroupCell' | 'generateGroupCell'> {
  params: GridValueFormatterParams<any>;
}

type TAgregationModel = NonNullable<NonNullable<TypeTable['initialState']>['aggregation']>['model'];
